<script lang="ts" module>
	export type TooltipPlacement =
		| 'top'
		| 'top-start'
		| 'top-end'
		| 'bottom'
		| 'bottom-start'
		| 'bottom-end'
		| 'left'
		| 'left-start'
		| 'left-end'
		| 'right'
		| 'right-start'
		| 'right-end';

	export interface TooltipProps {
		openDelay?: number;
		forceOpen?: boolean;
		tabIndex?: number;
		children?: import('svelte').Snippet;
		contentSlot?: import('svelte').Snippet;
		class?: string;
		dataCy: string;
		content?: string;
		classes?: { content?: string };
		placement?: TooltipPlacement;
		disabled?: boolean;
	}
</script>

<script lang="ts">
	import { onMount } from 'svelte';
	import { fade } from 'svelte/transition';
	import { twMerge } from 'tailwind-merge';
	import { Txt } from '../Txt';

	let {
		dataCy,
		content = '',
		placement = 'top',
		classes = {},
		children,
		openDelay = 0,
		forceOpen = false,
		tabIndex = 0,
		contentSlot,
		disabled,
		...rest
	}: TooltipProps = $props();

	let dataCyPrefix = $derived(`${dataCy}-tooltip`);
	let open = $state(false);
	let openTimeout: ReturnType<typeof setTimeout> | null = null;
	let tooltipEl: HTMLDivElement | undefined = $state();
	let triggerEl: HTMLSpanElement | undefined = $state();
	let overrideStyle = $state('');
	let overridePlacement = $state(false);

	const GAP = 8;
	const SCREEN_PAD = 8;

	function show() {
		if (openDelay > 0) {
			openTimeout = setTimeout(() => {
				open = true;
			}, openDelay);
		} else {
			open = true;
		}
	}

	function hide() {
		if (openTimeout) {
			clearTimeout(openTimeout);
			openTimeout = null;
		}
		open = false;
		overrideStyle = '';
		overridePlacement = false;
	}

	$effect(() => {
		if (!tooltipEl || !triggerEl) return;
		overrideStyle = '';
		overridePlacement = false;

		requestAnimationFrame(() => {
			if (!tooltipEl || !triggerEl) return;
			const tt = tooltipEl.getBoundingClientRect();
			const trigger = triggerEl.getBoundingClientRect();

			// Nothing measured yet (e.g. before layout): keep the declared placement
			// rather than clamping against a zero-size rect.
			if (!tt.width && !tt.height) return;

			// If overflowing top, switch to below the trigger
			if (tt.top < 0) {
				overridePlacement = true;
				const top = trigger.bottom + GAP;
				const left = trigger.left + trigger.width / 2 - tt.width / 2;
				const clampedLeft = Math.max(
					SCREEN_PAD,
					Math.min(left, window.innerWidth - SCREEN_PAD - tt.width)
				);
				overrideStyle = `position: fixed; top: ${top}px; left: ${clampedLeft}px;`;
				return;
			}

			// Clamp horizontally
			if (tt.left < SCREEN_PAD || tt.right > window.innerWidth - SCREEN_PAD) {
				const clampedLeft = Math.max(
					SCREEN_PAD,
					Math.min(tt.left, window.innerWidth - SCREEN_PAD - tt.width)
				);
				overrideStyle = `position: fixed; top: ${tt.top}px; left: ${clampedLeft}px;`;
				overridePlacement = true;
			}
		});
	});

	onMount(() => {
		const handleScroll = () => {
			hide();
		};

		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	});

	const placementStyles: Record<TooltipPlacement, string> = {
		top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
		'top-start': 'bottom-full left-0 mb-2',
		'top-end': 'bottom-full right-0 mb-2',
		bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
		'bottom-start': 'top-full left-0 mt-2',
		'bottom-end': 'top-full right-0 mt-2',
		left: 'right-full top-1/2 -translate-y-1/2 mr-2',
		'left-start': 'right-full top-0 mr-2',
		'left-end': 'right-full bottom-0 mr-2',
		right: 'left-full top-1/2 -translate-y-1/2 ml-2',
		'right-start': 'left-full top-0 ml-2',
		'right-end': 'left-full bottom-0 ml-2'
	};
</script>

<span
	bind:this={triggerEl}
	role="button"
	tabindex={tabIndex}
	aria-label="show tooltip"
	data-cy={dataCyPrefix}
	class={twMerge('relative inline-block cursor-pointer', rest.class)}
	onmouseenter={show}
	onmouseleave={hide}
	onfocus={show}
	onblur={hide}
	onpointerdown={() => (open = true)}
>
	{@render children?.()}

	{#if (open || forceOpen) && !disabled}
		<div
			bind:this={tooltipEl}
			role="tooltip"
			transition:fade={{ duration: 50 }}
			class={twMerge(
				'z-50 max-w-[12rem] w-max rounded bg-gray-700 px-2 py-1',
				overridePlacement ? '' : `absolute ${placementStyles[placement]}`,
				classes.content
			)}
			style={overrideStyle}
			data-cy={`${dataCyPrefix}-content`}
		>
			{#if content}
				<Txt variant="label4" class="text-gray-0">{content}</Txt>
			{:else}
				{@render contentSlot?.()}
			{/if}
		</div>
	{/if}
</span>
